import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { useList } from '@hana/data';
import { mergeLeave, type MeeRequest, type RequestStatus } from './data';

// Thao tác giả lập: trạng thái đơn đổi trong bộ nhớ (chưa ghi DB), giữ khi chuyển màn.
let overrides: ReadonlyMap<string, RequestStatus> = new Map();
let added: readonly MeeRequest[] = [];
const subs = new Set<() => void>();
const set = (id: string, st: RequestStatus | null) => {
  const m = new Map(overrides);
  if (st) m.set(id, st);
  else m.delete(id);
  overrides = m;
  subs.forEach((f) => f());
};
const addReq = (r: MeeRequest) => {
  added = [r, ...added];
  subs.forEach((f) => f());
  return () => {
    added = added.filter((x) => x.id !== r.id);
    subs.forEach((f) => f());
  };
};
const subscribe = (f: () => void) => {
  subs.add(f);
  return () => void subs.delete(f);
};

/** Đơn từ (dữ liệu mẫu) cùng đơn mới gửi và trạng thái đã đổi trong phiên. `pull` = rút đơn chờ duyệt, `submit` = gửi đơn mới; cả hai trả hàm hoàn tác. */
export function useMeeRequests() {
  const q = useList('meeRequest');
  // Đơn phép đọc theo phạm vi phiên đăng nhập (worker lọc): của mình / của nhóm mình quản lý. Loại đơn khác vẫn từ `meeRequest`.
  const mineQ = useList('meeLeaveMine');
  const teamQ = useList('meeLeaveTeam');
  const ov = useSyncExternalStore(subscribe, () => overrides);
  const add = useSyncExternalStore(subscribe, () => added);
  const merged = useMemo(() => mergeLeave(q.data?.items ?? [], mineQ.data?.items ?? [], teamQ.data?.items ?? []), [q.data, mineQ.data, teamQ.data]);
  const items = useMemo<MeeRequest[]>(() => [...add, ...merged.items].map((r) => (ov.has(r.id) ? { ...r, status: ov.get(r.id)! } : r)), [merged, ov, add]);
  const pull = useCallback((id: string) => {
    set(id, 'Cancelled');
    return () => set(id, null);
  }, []);
  /** Duyệt / từ chối đơn của nhóm; trả hàm hoàn tác. */
  const decide = useCallback((ids: readonly string[], st: 'Approved' | 'Rejected') => {
    ids.forEach((id) => set(id, st));
    return () => ids.forEach((id) => set(id, null));
  }, []);
  const submit = useCallback((r: MeeRequest) => addReq(r), []);
  const error = q.error ?? mineQ.error ?? teamQ.error;
  const isPending = q.isPending || mineQ.isPending || teamQ.isPending;
  const refetch = () => Promise.all([q.refetch(), mineQ.refetch(), teamQ.refetch()]);
  return { q: { error, isPending, refetch }, items, mineIds: merged.mineIds, teamIds: merged.teamIds, pull, submit, decide };
}
